import type { CustomerSafeTicket } from "@msc/shared";

export type SortKey = "updated" | "created" | "status" | "type" | "key";
export type GroupKey = "none" | "status" | "type" | "reporter";

/** Status, in dem der Ball beim Kunden liegt. */
export const WAITING_STATUS = "Warte auf Rückmeldung";

/**
 * Platzhalter für „ohne Bearbeiter" im Bearbeiter-Filter. Er reist durch die
 * URL (`?bearbeiter=-`) und muss deshalb eine Zeichenkette sein, die neben
 * echten Namen in derselben Liste stehen kann. Ein Mensch, der in Jira
 * wörtlich „-" heißt, wäre die Kollision; die gibt es nicht.
 */
export const UNASSIGNED_ASSIGNEE = "-";

export interface TicketView {
  /** Schnellsuche über Schlüssel und Titel. */
  q: string;
  /** AUSGESCHLOSSENE Status — leer heißt alle; neue Werte sind sofort sichtbar. */
  excludedStatus: string[];
  excludedTypes: string[];
  /** AUSGEWÄHLTE Ersteller — leer heißt alle. */
  reporters: string[];
  /**
   * AUSGEWÄHLTE Bearbeiter — leer heißt alle, `UNASSIGNED_ASSIGNEE` in der
   * Liste heißt „niemand zugewiesen". Eine Auswahl, keine Ausschlussliste:
   * Die typische Frage im Termin ist „was liegt bei X und Y", nicht „X
   * ausblenden". Mehrere gleichzeitig (Bastian, 2026-09-22), wie bei den
   * Erstellern.
   */
  assignees: string[];
  sort: SortKey;
  group: GroupKey;
}

export const DEFAULT_VIEW: TicketView = {
  q: "",
  excludedStatus: [],
  excludedTypes: [],
  reporters: [],
  assignees: [],
  sort: "updated",
  group: "status",
};

export interface TicketGroup {
  label: string;
  tickets: CustomerSafeTicket[];
}

export interface TicketViewResult {
  visible: CustomerSafeTicket[];
  groups: TicketGroup[] | null;
  counts: {
    status: Record<string, number>;
    type: Record<string, number>;
    reporter: Record<string, number>;
    /** Je Bearbeiter; Vorgänge ohne Bearbeiter zählt `unassigned`. */
    assignee: Record<string, number>;
    unassigned: number;
    waiting: number;
    inProgress: number;
  };
}

const byDateDesc = (a: string, b: string) => new Date(b).getTime() - new Date(a).getTime();

/** Gruppen: „In Arbeit" immer zuerst, „Ausgesetzt" immer zuletzt, dazwischen alphabetisch. */
const rank = (label: string) => (label === "In Arbeit" ? 0 : label === "Ausgesetzt" ? 2 : 1);

/** Die Gruppe eines Tickets nach `group`; `null` ohne Gruppierung. */
export function groupLabelOf(t: CustomerSafeTicket, group: GroupKey): string | null {
  switch (group) {
    case "none": return null;
    case "status": return t.status;
    case "reporter": return t.reporterName ?? "—";
    case "type": return t.issueType;
  }
}

/** Reine Funktion: Filter → Sortierung → Gruppierung; Zähler über die GELADENE Menge. */
export function applyTicketView(tickets: CustomerSafeTicket[], view: TicketView): TicketViewResult {
  const counts = { status: {} as Record<string, number>, type: {} as Record<string, number>, reporter: {} as Record<string, number>, assignee: {} as Record<string, number>, unassigned: 0, waiting: 0, inProgress: 0 };
  for (const t of tickets) {
    counts.status[t.status] = (counts.status[t.status] ?? 0) + 1;
    counts.type[t.issueType] = (counts.type[t.issueType] ?? 0) + 1;
    if (t.reporterName) counts.reporter[t.reporterName] = (counts.reporter[t.reporterName] ?? 0) + 1;
    if (t.assigneeName) counts.assignee[t.assigneeName] = (counts.assignee[t.assigneeName] ?? 0) + 1;
    else counts.unassigned += 1;
  }

  const q = view.q.trim().toLowerCase();
  const visible = tickets.filter(
    (t) =>
      !view.excludedStatus.includes(t.status) &&
      !view.excludedTypes.includes(t.issueType) &&
      (view.reporters.length === 0 || (t.reporterName !== null && view.reporters.includes(t.reporterName))) &&
      (view.assignees.length === 0 ||
        view.assignees.some((a) => (a === UNASSIGNED_ASSIGNEE ? !t.assigneeName : t.assigneeName === a))) &&
      (q === "" || t.key.toLowerCase().includes(q) || t.title.toLowerCase().includes(q)),
  );

  switch (view.sort) {
    case "updated": visible.sort((a, b) => byDateDesc(a.updatedIso, b.updatedIso)); break;
    case "created": visible.sort((a, b) => byDateDesc(a.createdIso, b.createdIso)); break;
    case "status": visible.sort((a, b) => a.status.localeCompare(b.status, "de")); break;
    case "type": visible.sort((a, b) => a.issueType.localeCompare(b.issueType, "de")); break;
    case "key": visible.sort((a, b) => a.key.localeCompare(b.key, "de", { numeric: true })); break;
  }

  counts.waiting = visible.filter((t) => t.status === WAITING_STATUS).length;
  counts.inProgress = visible.filter((t) => t.statusCategory === "indeterminate").length;

  if (view.group === "none") return { visible, groups: null, counts };

  const map = new Map<string, CustomerSafeTicket[]>();
  for (const t of visible) {
    const label = groupLabelOf(t, view.group) ?? "";
    const bucket = map.get(label);
    if (bucket) bucket.push(t);
    else map.set(label, [t]);
  }
  const groups = [...map.entries()]
    .sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0], "de"))
    .map(([label, items]) => ({ label, tickets: items }));
  return { visible, groups, counts };
}

/**
 * Die Gruppierung der Liste, wie die Präsentation sie übernimmt (#278): je
 * Ticket das Gruppenetikett, unter dem es gerade steht. `undefined` ohne
 * Gruppierung — dann läuft das Deck ohne Überschriften.
 */
export function groupingForPrep(
  result: TicketViewResult,
  group: GroupKey,
): { by: GroupKey; labels: Record<string, string> } | undefined {
  if (!result.groups || group === "none") return undefined;
  return {
    by: group,
    labels: Object.fromEntries(result.groups.flatMap((g) => g.tickets.map((t) => [t.key, g.label]))),
  };
}

/**
 * Reihenfolge, in der `TicketList` tatsächlich rendert: mit Gruppierung die
 * Gruppenreihenfolge (Gruppen hintereinander), sonst die flache `visible`-Liste.
 * Navigation und Auto-Auswahl müssen dieser Reihenfolge folgen, nicht `visible`.
 */
export function orderedTickets(result: TicketViewResult): CustomerSafeTicket[] {
  return result.groups ? result.groups.flatMap((g) => g.tickets) : result.visible;
}
